import { Avatar, Tooltip, type AvatarProps } from '@fluentui/react-components';
import { cn } from '@/lib/cn';
import { monogramFrom } from '@/lib/color';

/**
 * Person primitives.
 *
 * `UserAvatar` wraps Fluent's `Avatar` with our monogram rules; `UserChip`
 * composes it with name/email for organizer and attendee lists.
 */

export interface UserAvatarProps {
  name: string;
  avatarUrl?: string | null;
  email?: string | null;
  size?: AvatarProps['size'];
  className?: string;
}

export function UserAvatar({
  name,
  avatarUrl,
  size = 24,
  className,
}: UserAvatarProps): JSX.Element {
  return (
    <Avatar
      className={className}
      name={name}
      size={size}
      color="colorful"
      initials={monogramFrom(name)}
      image={avatarUrl ? { src: avatarUrl } : undefined}
      aria-label={name}
    />
  );
}

export interface UserChipProps extends UserAvatarProps {
  /** Highlights the current user's own reservations. */
  isYou?: boolean;
  /** Hides the text, leaving only the avatar with a tooltip. */
  avatarOnly?: boolean;
  secondaryText?: string | null;
}

export function UserChip({
  name,
  email,
  avatarUrl,
  size = 24,
  isYou = false,
  avatarOnly = false,
  secondaryText,
  className,
}: UserChipProps): JSX.Element {
  const label = isYou ? `${name} (tú)` : name;

  if (avatarOnly) {
    return (
      <Tooltip content={email ? `${label} · ${email}` : label} relationship="label" withArrow>
        <span className={cn('inline-flex', className)}>
          <UserAvatar name={name} avatarUrl={avatarUrl} size={size} />
        </span>
      </Tooltip>
    );
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <UserAvatar name={name} avatarUrl={avatarUrl} size={size} />
      <span className="min-w-0">
        <span
          className={cn(
            'block truncate text-body',
            isYou ? 'font-semibold text-ink' : 'text-ink-secondary',
          )}
        >
          {label}
        </span>
        {secondaryText ?? email ? (
          <span className="block truncate text-caption text-ink-muted">
            {secondaryText ?? email}
          </span>
        ) : null}
      </span>
    </span>
  );
}
